import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import { InputComponent } from '../../shared/ui/input/input.component';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { REGEX_PASSWORD_ALLSPECIAL_CHARACTER } from 'sources/apps/front-mgr/src/app/core/constants/constants';
import { ActivatedRoute, Router } from '@angular/router';
import { RoutePaths } from 'sources/apps/front-mgr/src/app/core/constants/route-paths';
import { CardHeaderTitleSubtitleComponent } from '../../shared/ui/card-header-title-subtitle/card-header-title-subtitle.component';
import { AuthVerificationHandlerComponent } from '../../shared/components/auth-verification-handler/auth-verification-handler.component';
import { Subscription } from 'rxjs';
import { MustMatch } from '../../shared/validators/must-match.validator';
import { AuthenticationService } from '../services/authentication.service';
import { SpinnerService } from '../../../core/services/spinner.service';
import { ResetPasswordRestoreBody } from '../type/password';
import { RecaptchaService } from '../../shared/services/recaptch.service';
import { DataSessionService } from '../../../core/services/data-session.service';
import { DataLayerService } from '../../../core/services/data-layer.service';

@Component({
  selector: 'app-forgot-password-regeneration',
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    TranslateModule,
    InputComponent,
    ReactiveFormsModule,
    CardHeaderTitleSubtitleComponent,
    AuthVerificationHandlerComponent,
  ],
  templateUrl: './forgot-password-regeneration.component.html',
  styleUrl: './forgot-password-regeneration.component.scss',
})
export class ForgotPasswordRegenerationComponent {
  forgotPassRegForm!: FormGroup;
  showForm: boolean = false;
  showResponse: boolean = false;
  subscription: Subscription = new Subscription();
  error!: string;
  token!: string;
  process!: AuthVerifMapKeys;
  screenName = 'forgot-password-regeneration';

  constructor(
    private readonly _formBuilder: FormBuilder,
    private readonly _router: Router,
    private readonly _authenticationService: AuthenticationService,
    private readonly _spinnerService: SpinnerService,
    private readonly _recaptchaService: RecaptchaService,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _dataSessionService: DataSessionService,
    private readonly _dataLayerService: DataLayerService
  ) {
    this.token = this._activatedRoute.snapshot.queryParamMap.get('token') ?? '';

    this.forgotPassRegForm = this._formBuilder.group(
      {
        password: new FormControl('', {
          validators: [
            Validators.pattern(REGEX_PASSWORD_ALLSPECIAL_CHARACTER),
            Validators.required,
          ],
          updateOn: 'change',
        }),
        validation_password: new FormControl('', {
          validators: [Validators.required],
          updateOn: 'change',
        }),
      },
      {
        validator: MustMatch('password', 'validation_password'),
      }
    );
  }

  ngOnInit(): void {
    this._dataLayerService.pushEventPageView(this.screenName);
    this.validateToken();
  }

  async submitForm(): Promise<void> {
    this._dataLayerService.pushEventClickButton(
      this.screenName,
      'accept',
      'forgot-password-regeneration.btn-accept'
    );
    this._spinnerService.show();
    const body: ResetPasswordRestoreBody = {
      token: this.token,
      newPassword: this.forgotPassRegForm.value.password,
    };

    const email = this.forgotPassRegForm.value;
    try {
      const token = await this._recaptchaService.reCAPTCHA('PASSWORD_RESTORE');

      const sub = this._authenticationService
        .postResetPasswordRestore(body)
        .subscribe({
          next: (response) => {
            this.process = this._authenticationService.getPassStatus('EXC03');
            this._dataLayerService.pushEventSubmit(
              this.screenName,
              'reset_password_restore',
              undefined,
              'body button'
            );
          },
          error: (err) => {
            if (err.error.state === 'EXC07') {
              // show modal recaptcha y reload form
              this._dataSessionService.showModalRecaptchaError(() =>
                this.reloadForm()
              );
            } else if (err.status == 400) {
              this.process = this._authenticationService.getPassStatus(
                err.error.state
              );
            } else {
              this.process = this._authenticationService.getPassStatus('EXC05');
            }
            this._dataLayerService.pushEventSubmitError(
              this.screenName,
              'reset_password_restore',
              this.process ?? err.status
            );
            this.showForm = false;
            this.showResponse = true;
            this._spinnerService.hide();
          },
          complete: () => {
            this.showForm = false;
            this.showResponse = true;
            this._spinnerService.hide();
          },
        });

      this.subscription.add(sub);
    } catch (error) {
      this._spinnerService.hide();
      this._dataLayerService.pushEventSubmitError(
        this.screenName,
        'recaptcha_error',
        'reset-password-restore',
        'Error executing reCAPTCHA from forgot password regeneration form'
      );
      console.error('Error executing reCAPTCHA', error);
    }
  }

  validateToken(): void {
    if (this.token) {
      this.showForm = true;
    } else {
      this.handleRedirect();
    }
  }

  async handleRedirect(): Promise<void> {
    this._dataLayerService.pushEventClickLink(
      this.screenName,
      'back',
      'forgot-password-regeneration.btn-back'
    );
    await this._router.navigate([`${RoutePaths.login}`]);
  }

  goBackFromVerification(e: boolean): void {
    const route =
      this.process !== 'tokenPassError'
        ? `${RoutePaths.login}`
        : `${RoutePaths.changePassword}`;
    this._router.navigate([route]);
  }

  reloadForm(): void {
    this.forgotPassRegForm.reset();
    this.error = '';
    this._spinnerService.hide();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
